import {
    format,
    ISO_DATE_ONLY_FORMAT,
    zodDateOfBirthValidation,
    zodFirstNameValidation,
    zodLastNameValidation,
} from '@fruitful/fruitils';
import { PersistedUserEvent } from '@fruitful/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLocalSearchParams, useRouter } from 'expo-router';
import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
    AlertTriangleIcon,
    BodySmallRegular,
    Button,
    ButtonVariant,
    CheckIcon,
    ControlledDateTextInput,
    ControlledInput,
    Icon,
    useToastController,
    View,
    XStack,
    YStack,
} from 'smoothie';
import z from 'zod';

import BackgroundImage from '../../../../assets/images/backgrounds/orchard.png';
import {
    GeneralDisclosuresFootnote,
    InvestmentManagementAgreementDisclosure,
    POLICY_DISCLOSURES,
    PolicyType,
} from '../../../banking/components/Disclosures';
import {
    formatWithMask,
    PHONE_NUMBER_MASK,
    VALID_PHONE_NUMBER_MASK,
} from '../../../common/utils/maskUtils';
import { useLogUserEvent } from '../../../events/hooks/useLogUserEvent';
import { captureException, captureWarning } from '../../../logger/Logger';
import { useUser } from '../../../user/providers/UserProvider';
import { fetchDeviceToken, resetPassword } from '../../api/AuthApi';
import { SESSION_DURATION_MINUTES } from '../../constants';
import { storeDeviceToken } from '../../helpers';
import { useUpdateMemberInfo } from '../../hooks/start-membership/useUpdateMemberInfo';
import { useStytch } from '../../hooks/useAuth';
import { PasswordStrengthChecker } from '../PasswordStrengthChecker';
import ShowPasswordToggle from '../ShowPasswordToggle';
import { shouldShowIOSAppPage } from './GetTheApp';
import { JoinMembershipTestIds } from './JoinMembershipTestIds';
import { SplitScreenHighlightLayout } from './SplitScreenHighlightLayout';

const joinMembershipSchema = z.object({
    password: z.string().min(1, 'Please enter a password.'),
    firstName: zodFirstNameValidation,
    lastName: zodLastNameValidation,
    phoneNumber: z
        .string()
        .min(10, 'Please enter a valid phone number.')
        .regex(VALID_PHONE_NUMBER_MASK, 'Please enter a valid phone number'),
    dateOfBirth: zodDateOfBirthValidation,
});

// Setting the full type definition here manually because z.infer<typeof startMembershipSchema> wasn't working as expected
type JoinMembershipSchema = z.infer<typeof joinMembershipSchema>;

const registerDeviceToken = async (
    stytchUserId: string,
    stytchSessionId: string,
) => {
    // This endpoint currently depends on having a fruitful user account
    const deviceToken = await fetchDeviceToken({
        stytchId: stytchUserId,
        sessionId: stytchSessionId,
        isFirstLogin: true,
    });

    if (deviceToken == null) {
        captureWarning('unable to verify device with jwt');
    } else {
        storeDeviceToken(deviceToken);
    }
};

export const JoinMembership = () => {
    const router = useRouter();
    const toast = useToastController();
    const stytchClient = useStytch();

    const { user, refetch: refetchUser } = useUser();
    const { token: stychToken } = useLocalSearchParams<{ token?: string }>();
    const { updateMemberInfo } = useUpdateMemberInfo();

    const [isPartnerEnrollmentInProgress, setIsPartnerEnrollmentInProgress] =
        useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [authError, setAuthError] = useState<Error | null>(null);
    const { masked: formattedPhoneNumber, unmasked } = formatWithMask(
        PHONE_NUMBER_MASK,
        '',
    );
    const [unmaskedPhoneNumber, setUnmaskedPhoneNumber] = useState(unmasked);
    const [isContactInfoReadyForUpdate, setIsContactInfoReadyForUpdate] =
        useState(false);
    const [contactInfo, setContactInfo] = useState({
        firstName: '',
        lastName: '',
        dateOfBirth: '',
        phoneNumber: '',
    });

    /* Redirect if no stytch token is present */
    if (stychToken == null || stychToken === '') {
        router.push('/');
        return;
    }

    const form = useForm<JoinMembershipSchema>({
        resolver: zodResolver(joinMembershipSchema),
        defaultValues: {
            password: '',
            firstName: '',
            lastName: '',
            dateOfBirth: '',
            phoneNumber: formattedPhoneNumber,
        },
    });

    const watchedPassword = form.watch('password');

    const onPasswordStrengthChange = useCallback(() => {
        setAuthError(null);
    }, []);

    const { logUserEvent } = useLogUserEvent();

    const continueToNextPage = useCallback(() => {
        if (shouldShowIOSAppPage()) {
            router.push('/start-membership/get-the-app');
        } else {
            router.push('/home');
        }
    }, [router]);

    const showErrorToast = () => {
        toast.show('Error joining membership', {
            icon: (
                <View backgroundColor="$red100" padding={8} borderRadius={4}>
                    <Icon icon={AlertTriangleIcon} color="$red800" />
                </View>
            ),
        });
    };

    /**
     * Run the user update as an effect to ensure the UserProvider properly
     * reacted to the session/state changes and was able to (1) fetch the user
     * and (2) set the proper auth state uninhibited.
     *
     * Once that is complete, update the contact info and proceed.
     */
    useEffect(() => {
        if (user != null && isContactInfoReadyForUpdate) {
            updateMemberInfo({
                userId: user?.id,
                ...contactInfo,
                onSuccess: async () => {
                    setIsContactInfoReadyForUpdate(false);
                    if (refetchUser) await refetchUser();
                    continueToNextPage();
                    toast.show('Joined membership!', {
                        icon: (
                            <View
                                backgroundColor="$green300"
                                padding={8}
                                borderRadius={4}
                            >
                                <Icon icon={CheckIcon} color="$green1100" />
                            </View>
                        ),
                    });
                    setIsPartnerEnrollmentInProgress(false);
                },
                onError: () => {
                    showErrorToast();
                    setIsPartnerEnrollmentInProgress(false);
                },
            });
        }
    }, [user, contactInfo, isContactInfoReadyForUpdate]);

    const onFormSubmit = async (formData: JoinMembershipSchema) => {
        setAuthError(null);
        setIsPartnerEnrollmentInProgress(true);

        try {
            const { password, firstName, lastName, dateOfBirth } = formData;
            const { session_token, session_jwt } = await resetPassword({
                password,
                token: stychToken,
            });

            logUserEvent(
                PersistedUserEvent.DISCLOSURE_ACKNOWLEDGEMENT_INVESTMENT_MANAGEMENT_AGREEMENT,
                POLICY_DISCLOSURES[PolicyType.INVESTMENT_MANAGEMENT_AGREEMENT],
            );

            if (session_token != null && session_jwt != null) {
                stytchClient.session.updateSession({
                    session_token,
                    session_jwt,
                });

                const { session: stytchSession } =
                    await stytchClient.session.authenticate({
                        session_duration_minutes: SESSION_DURATION_MINUTES,
                    });

                const stytchUser = await stytchClient.user.get();
                if (stytchUser?.user_id == null || stytchSession == null) {
                    showErrorToast();
                    return;
                }

                await registerDeviceToken(
                    stytchUser.user_id,
                    stytchSession.session_id,
                );

                setContactInfo({
                    firstName,
                    lastName,
                    /* Format with timestamp for backend call */
                    dateOfBirth: `${format(dateOfBirth, ISO_DATE_ONLY_FORMAT)}T00:00:00Z`,
                    phoneNumber: `+1${unmaskedPhoneNumber}`,
                });

                setIsContactInfoReadyForUpdate(true);
            }
        } catch (err) {
            setIsContactInfoReadyForUpdate(false);
            setIsPartnerEnrollmentInProgress(false);
            captureException(err);
            showErrorToast();
        }
    };

    const title = 'Welcome to Fruitful';
    const subtitle = 'Complete your profile to get started.';
    const DISCLOSURE_ACKNOWLEDGEMENT_BUTTON_LABEL = 'Agree and continue';

    return (
        <SplitScreenHighlightLayout
            backgroundImage={BackgroundImage}
            title={title}
            subtitle={subtitle}
            includeLogo
            disclosures={<Disclosures />}
            scrollViewTestID={JoinMembershipTestIds.mainScrollView}
        >
            <YStack testID={JoinMembershipTestIds.page} width="100%" gap={24}>
                <YStack width="100%" gap={16}>
                    {watchedPassword.length > 0 &&
                        authError?.message != null &&
                        authError?.message.length > 0 && (
                            <BodySmallRegular color="$red1100">
                                {authError?.message}
                            </BodySmallRegular>
                        )}
                    <XStack gap={12}>
                        <ControlledInput
                            containerStyle={{ flex: 1 }}
                            label="First name"
                            name="firstName"
                            placeholder="Your first name"
                            control={form.control}
                            register={form.register}
                            errors={form.formState.errors}
                            autoCapitalize="words"
                            autoComplete="name-given"
                            textContentType="name"
                            enterKeyHint="next"
                            onSubmitEditing={() => {
                                form.setFocus('lastName');
                            }}
                            disabled={isPartnerEnrollmentInProgress}
                        />
                        <ControlledInput
                            containerStyle={{ flex: 1 }}
                            label="Last name"
                            name="lastName"
                            placeholder="Your last name"
                            control={form.control}
                            register={form.register}
                            errors={form.formState.errors}
                            autoCapitalize="words"
                            autoComplete="name-family"
                            textContentType="name"
                            enterKeyHint="next"
                            onSubmitEditing={() => {
                                form.setFocus('dateOfBirth');
                            }}
                            disabled={isPartnerEnrollmentInProgress}
                        />
                    </XStack>
                    <ControlledDateTextInput
                        label="Date of birth"
                        name="dateOfBirth"
                        control={form.control}
                        register={form.register}
                        errors={form.formState.errors}
                        onSubmitEditing={() => {
                            form.setFocus('phoneNumber');
                        }}
                        disabled={isPartnerEnrollmentInProgress}
                    />
                    <ControlledInput
                        label="Phone number"
                        name="phoneNumber"
                        control={form.control}
                        register={form.register}
                        errors={form.formState.errors}
                        formatValueOnChange={(_, value) => {
                            const { masked, unmasked } = formatWithMask(
                                PHONE_NUMBER_MASK,
                                value,
                            );
                            setUnmaskedPhoneNumber(unmasked);
                            return masked;
                        }}
                        placeholder="(123) 456-7890"
                        autoComplete="tel-national"
                        inputMode="tel"
                        keyboardType="numbers-and-punctuation"
                        disabled={isPartnerEnrollmentInProgress}
                    />
                </YStack>
                <View>
                    <ControlledInput
                        label="Password"
                        name="password"
                        autoCapitalize="none"
                        autoComplete="new-password"
                        placeholder="Set your password"
                        inputMode="text"
                        keyboardType="default"
                        textContentType="password"
                        secureTextEntry={!showPassword}
                        control={form.control}
                        register={form.register}
                        errors={form.formState.errors}
                        disabled={isPartnerEnrollmentInProgress}
                        onSubmitEditing={() => {
                            form.setFocus('firstName');
                        }}
                        rightSideContent={
                            <ShowPasswordToggle
                                showPassword={showPassword}
                                setShowPassword={setShowPassword}
                            />
                        }
                    />
                    <PasswordStrengthChecker
                        password={watchedPassword}
                        onStrengthCheckChange={onPasswordStrengthChange}
                    />
                </View>
                <View paddingHorizontal={4}>
                    <InvestmentManagementAgreementDisclosure
                        acknowledgementButtonLabel={
                            DISCLOSURE_ACKNOWLEDGEMENT_BUTTON_LABEL
                        }
                    />
                </View>
                <Button
                    testID={JoinMembershipTestIds.continueButton}
                    disabled={isPartnerEnrollmentInProgress}
                    variant={ButtonVariant.PRIMARY}
                    tone="positive"
                    width="full"
                    onPress={form.handleSubmit(onFormSubmit)}
                    text={DISCLOSURE_ACKNOWLEDGEMENT_BUTTON_LABEL}
                />
            </YStack>
        </SplitScreenHighlightLayout>
    );
};

const Disclosures = () => {
    return (
        <View
            paddingTop={32}
            width="98%"
            maxWidth={600}
            alignSelf="center"
            userSelect="none"
            $sm={{ paddingTop: 64 }}
            $lg={{ paddingTop: 24 }}
        >
            <GeneralDisclosuresFootnote />
        </View>
    );
};
